import { type StructuredContentDocument } from '@repo/contracts/multi-use/structured-content/types';
import { env } from '@repo/env';
import { css } from '@repo/pandacss/styled-system/css';
import type { CSSProperties } from 'react';
import { SPACING_TOKEN_MAP } from './constants';
import type { SpacingToken } from './types';

function generateSrcSet({
  dprs = [1.5, 2, 3],
  quality = 85,
  source,
  height,
  width,
}: {
  dprs?: number[];
  quality?: number;
  source?: string;
  height: number;
  width: number;
}) {
  if (!source) return;
  if (source.endsWith('.svg')) return source;

  return dprs
    .map((d, i) => {
      const url = new URL(source);
      if (!url.hostname.includes('nerdwallet')) return null;

      let updatedUrl = source
        .replace(/width=\d+/, '')
        .replace(/height=\d+/, '')
        .replace(/quality=\d+/, '')
        .replace(/dpr=\d+/, '')
        .replace(/format=\s+/, '')
        .replace(/resize=/, '')
        .replace('cdn-cgi/image/', '')
        .replace('https://www.nerdwallet.com', '')
        .replace('https://stage.nerdwallet.biz', '')
        .replace('/,/', '/');

      if (!updatedUrl.startsWith('/')) updatedUrl = `/${updatedUrl}`;

      const path = `${env.NEXT_PUBLIC_NW_URL}/cdn-cgi/image/width=${width},height=${height},quality=${quality},format=webp,fit=contain,dpr=${d},${updatedUrl}`;
      return path ? `${path} ${dprs[i]}x` : '';
    })
    .filter(Boolean)
    .join(', ');
}

type Props = StructuredContentDocument<{
  align?: 'center' | 'left' | 'right';
  alt?: string;
  caption?: string;
  id: string;
  naturalHeight: number;
  naturalWidth: number;
  spacing?: {
    bottom: SpacingToken;
    top: SpacingToken;
  };
  src: string;
  title?: string;
}>;

export function ContentImage({ props }: Props) {
  const imageSrcSet = generateSrcSet({
    source: props.src,
    width: props.naturalWidth,
    height: props.naturalHeight,
  });

  if (!imageSrcSet) return null;

  const image = (
    <img
      alt={props.alt}
      id={props.id}
      srcSet={imageSrcSet}
      loading="lazy"
      className={css({
        objectFit: 'contain',
        width: {
          base: 'full',
          sm: 'var(--img-natural-width)',
        },
        height: {
          base: 'full',
          sm: 'var(--img-natural-height)',
        },
      })}
      style={
        {
          '--img-natural-width': `${props.naturalWidth}px`,
          '--img-natural-height': `${props.naturalHeight}px`,
        } as CSSProperties
      }
    />
  );

  return (
    <span
      className={css({
        display: 'block',
        marginInlineStart: 'auto',
        marginInlineEnd: 'auto',
        float: {
          sm: 'var(--img-float)',
        },
        marginRight: {
          sm: 'var(--img-margin-right)',
        },
        marginLeft: {
          sm: 'var(--img-margin-left)',
        },
      })}
      style={
        {
          maxWidth: props.naturalWidth,
          '--img-float': props.align === 'center' ? 'none' : (props.align ?? 'none'),
          '--img-margin-right': props.align === 'left' ? '2rem' : 'auto',
          '--img-margin-left': props.align === 'right' ? '2rem' : 'auto',
          ...(props.spacing && {
            bottom: SPACING_TOKEN_MAP[props.spacing.bottom] || '0',
            top: SPACING_TOKEN_MAP[props.spacing.top] || '0',
          }),
        } as CSSProperties
      }
    >
      {props.caption ? (
        <span className={css({ display: 'flex', flexDirection: 'column' })}>
          {image}
          <span
            className={css({
              textStyle: 'bodySmall',
              color: 'neutral.light',
              lineHeight: 'normal',
            })}
          >
            {props.caption}
          </span>
        </span>
      ) : (
        image
      )}
    </span>
  );
}
